import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/client';
import toast from 'react-hot-toast';
import {
  AlertTriangle, TrendingUp, TrendingDown, Minus, Clock, User,
  Activity, ChevronRight, Search, Loader2, CheckCircle, AlertCircle,
  Stethoscope, Shield, RefreshCw, ArrowRight, Heart, X, Send,
  FileText, Phone, MessageSquare, Calendar, Pill, ExternalLink
} from 'lucide-react';

const riskCfg = {
  critical: { bg: 'bg-red-50',    text: 'text-red-600',    border: 'border-red-100',    dot: 'bg-red-600',    ring: 'ring-red-500/20',    hex: '#ba1a1a' },
  high:     { bg: 'bg-orange-50',  text: 'text-orange-600', border: 'border-orange-100', dot: 'bg-orange-500', ring: 'ring-orange-500/20', hex: '#F97316' },
  medium:   { bg: 'bg-amber-50',   text: 'text-amber-700',  border: 'border-amber-100',  dot: 'bg-amber-500', ring: 'ring-amber-500/20',  hex: '#d97706' },
  low:      { bg: 'bg-emerald-50', text: 'text-emerald-600',border: 'border-emerald-100',dot: 'bg-emerald-500',ring: 'ring-emerald-500/20',hex: '#00696b' },
};

const getRiskKey = (score) => {
  if (score >= 80) return 'critical';
  if (score >= 60) return 'high';
  if (score >= 40) return 'medium';
  return 'low';
};

const getRiskLabel = (score) => {
  if (score >= 80) return 'Critical';
  if (score >= 60) return 'High';
  if (score >= 40) return 'Medium';
  return 'Low';
};

const PriorityDashboard = () => {
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [sortBy, setSortBy] = useState('risk');
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();

  useEffect(() => { fetchHighRiskPatients(); }, []);

  const fetchHighRiskPatients = async () => {
    try {
      setLoading(true);
      const response = await api.get('/risk-assessments/pending?minScore=60&limit=50');
      if (response.data.success) setPatients(response.data.data);
    } catch (error) {
      console.error('Error fetching high-risk patients:', error);
      toast.error('Failed to load priority patients');
    } finally {
      setLoading(false);
    }
  };

  const filteredPatients = useMemo(() => patients
    .filter(p => {
      if (filter === 'critical') return p.riskScore.overall >= 80;
      if (filter === 'high') return p.riskScore.overall >= 60 && p.riskScore.overall < 80;
      if (filter === 'medium') return p.riskScore.overall >= 40 && p.riskScore.overall < 60;
      if (filter === 'low') return p.riskScore.overall < 40;
      return true;
    })
    .filter(p => {
      if (!searchQuery) return true;
      const q = searchQuery.toLowerCase();
      return p.patient.name?.toLowerCase().includes(q) || p.patient.email?.toLowerCase().includes(q);
    })
    .sort((a, b) => {
      if (sortBy === 'risk') return b.riskScore.overall - a.riskScore.overall;
      if (sortBy === 'time') return new Date(b.lastActivity) - new Date(a.lastActivity);
      if (sortBy === 'name') return a.patient.name?.localeCompare(b.patient.name);
      return 0;
    }), [patients, filter, searchQuery, sortBy]);

  const getTrendIcon = (trend) => {
    if (trend === 'improving') return <TrendingDown className="w-3.5 h-3.5 text-emerald-500" />;
    if (trend === 'worsening') return <TrendingUp className="w-3.5 h-3.5 text-red-500" />;
    if (trend === 'critical') return <AlertTriangle className="w-3.5 h-3.5 text-red-600" />;
    return <Minus className="w-3.5 h-3.5 text-slate-500" />;
  };

  const getRecommendedAction = (assessment) => {
    if (!assessment?.recommendedActions?.length) return null;
    return assessment.recommendedActions[0];
  };

  // ── Action panel state ──
  const [actionPanel, setActionPanel] = useState(null); // { patient, assessment, action }
  const [panelNote, setPanelNote] = useState('');
  const [executing, setExecuting] = useState(false);
  const panelRef = useRef(null);

  const openActionPanel = (patientRow, action, e) => {
    e.stopPropagation();
    setActionPanel({ patient: patientRow.patient, assessment: patientRow.assessment, riskScore: patientRow.riskScore, action });
    setPanelNote('');
  };

  const closePanel = () => { setActionPanel(null); setPanelNote(''); };

  // Close on Escape
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') closePanel(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const executeAction = async () => {
    if (!actionPanel) return;
    setExecuting(true);
    try {
      await api.post(`/risk-assessments/${actionPanel.assessment._id}/execute-action`, {
        actionType: actionPanel.action.actionType,
        patientId: actionPanel.patient._id,
        notes: panelNote || undefined,
      });
      toast.success(`Action recorded: ${actionPanel.action.actionType.replace(/_/g, ' ')}`);
      closePanel();
      fetchHighRiskPatients();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to execute action');
    } finally {
      setExecuting(false);
    }
  };

  const handleActionClick = (patientRow, action, e) => {
    e.stopPropagation();
    openActionPanel(patientRow, action, e);
  };

  const handleDismissAlert = async (assessment, patientId, e) => {
    e.stopPropagation();
    if (!assessment?.recommendedActions?.length) {
      toast.error('No action to dismiss');
      return;
    }
    const actionType = assessment.recommendedActions[0].actionType;
    try {
      await api.post(`/risk-assessments/${assessment._id}/dismiss-action`, {
        actionType,
        patientId,
        reason: 'Acknowledged from priority dashboard'
      });
      fetchHighRiskPatients();
      toast.success('Alert dismissed');
    } catch (error) {
      console.error('Error dismissing alert:', error);
      toast.error('Failed to dismiss alert');
    }
  };

  // Stat counts
  const criticalCount = patients.filter(p => p.riskScore.overall >= 80).length;
  const highCount = patients.filter(p => p.riskScore.overall >= 60 && p.riskScore.overall < 80).length;
  const worseningCount = patients.filter(p => p.assessment?.trend === 'worsening').length;

  if (loading) {
    return (
      <div className="space-y-8">
        <div className="h-10 w-72 rounded-xl animate-pulse bg-slate-100" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => <div key={i} className="h-32 rounded-2xl animate-pulse bg-slate-100" />)}
        </div>
        <div className="h-20 rounded-2xl animate-pulse bg-slate-100" />
        <div className="space-y-4">
          {[...Array(3)].map((_, i) => <div key={i} className="h-40 rounded-2xl animate-pulse bg-slate-100" />)}
        </div>
      </div>
    );
  }

  return (
    <>
    <div className="space-y-8">
      {/* ─── Page Header ─── */}
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-red-50">
              <Shield className="w-6 h-6 text-red-600" />
            </div>
            <div>
              <p className="text-xs font-extrabold text-slate-500 uppercase tracking-[0.2em]">Risk Management</p>
              <h1 className="font-headline text-4xl font-extrabold text-primary tracking-tight">Priority Queue</h1>
            </div>
          </div>
          <p className="text-sm text-slate-500 font-medium mt-1 ml-[60px]">
            Patients requiring immediate attention, ranked by risk score.
          </p>
        </div>
        <button
          onClick={fetchHighRiskPatients}
          className="flex items-center gap-2 bg-slate-900 text-white px-5 py-3 rounded-xl font-bold text-sm hover:bg-primary shadow-sm hover:shadow-primary/25 transition-all duration-300 active:scale-95 self-start lg:self-auto"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </button>
      </div>

      {/* ─── Stat Cards ─── */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Critical', value: criticalCount, color: '#ba1a1a', icon: AlertTriangle, pulse: criticalCount > 0 },
          { label: 'High Risk', value: highCount, color: '#F97316', icon: AlertCircle },
          { label: 'Worsening', value: worseningCount, color: '#d97706', icon: TrendingUp },
          { label: 'Total Monitored', value: patients.length, color: '#00478d', icon: Activity },
        ].map((s, i) => (
          <div key={i} className="bg-white rounded-2xl shadow-premium hover:shadow-premium-hover transition-all duration-500 p-7 border border-outline-variant/10 relative overflow-hidden">
            {s.pulse && s.value > 0 && (
              <div className="absolute top-4 right-4 w-2.5 h-2.5 rounded-full bg-red-500 animate-pulse" />
            )}
            <div className="flex items-start justify-between mb-4">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${s.color}12` }}>
                <s.icon className="w-6 h-6" style={{ color: s.color }} />
              </div>
            </div>
            <p className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-1">{s.label}</p>
            <h3 className="text-3xl font-extrabold text-slate-900 tracking-tighter">{s.value}</h3>
          </div>
        ))}
      </div>

      {/* ─── Filters Bar ─── */}
      <div className="bg-white rounded-2xl shadow-premium border border-outline-variant/10 p-5 relative overflow-hidden">
        <div className="absolute -top-16 -right-16 w-40 h-40 bg-primary/5 rounded-full blur-[80px] pointer-events-none" />
        <div className="flex flex-col md:flex-row gap-4 relative z-10">
          {/* Search */}
          <div className="flex-1 relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <input
              type="text"
              placeholder="Search patients by name or email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-11 pr-4 py-3 bg-slate-50 border border-outline-variant/20 rounded-xl text-sm font-medium text-slate-900 placeholder:text-slate-500 focus:bg-white focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
            />
          </div>

          {/* Filter pills */}
          <div className="flex items-center gap-2 flex-wrap">
            {[
              { key: 'all', label: 'All' },
              { key: 'critical', label: 'Critical' },
              { key: 'high', label: 'High' },
              { key: 'medium', label: 'Medium' },
              { key: 'low', label: 'Low' },
            ].map(f => (
              <button
                key={f.key}
                onClick={() => setFilter(f.key)}
                className={`px-4 py-2 rounded-full text-xs font-bold transition-all duration-300 ${
                  filter === f.key
                    ? 'bg-primary text-white shadow-sm'
                    : 'bg-slate-50 text-slate-600 border border-outline-variant/20 hover:bg-white hover:shadow-sm'
                }`}
              >
                {f.label}
              </button>
            ))}
            <div className="h-5 w-px bg-slate-200 mx-1" />
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-4 py-2 bg-slate-50 border border-outline-variant/20 rounded-full text-xs font-bold text-slate-600 focus:ring-2 focus:ring-primary/20 cursor-pointer"
            >
              <option value="risk">Sort: Risk</option>
              <option value="time">Sort: Activity</option>
              <option value="name">Sort: Name</option>
            </select>
          </div>
        </div>
      </div>

      {/* ─── Patient Cards ─── */}
      {filteredPatients.length === 0 ? (
        <div className="bg-white rounded-2xl shadow-premium border border-outline-variant/10 py-20 text-center relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-emerald-500/[0.03] to-transparent pointer-events-none" />
          <div className="relative z-10">
            <div className="w-16 h-16 rounded-2xl bg-emerald-50 flex items-center justify-center mx-auto mb-5">
              <CheckCircle className="w-8 h-8 text-emerald-500" />
            </div>
            <h3 className="font-headline text-xl font-bold text-slate-900 mb-2">All Clear</h3>
            <p className="text-sm text-slate-500 font-medium">No patients in this risk category requiring attention.</p>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredPatients.map((patient, idx) => {
            const score = patient.riskScore.overall;
            const key = getRiskKey(score);
            const cfg = riskCfg[key];
            const assessment = patient.assessment;
            const recommendedAction = getRecommendedAction(assessment);

            return (
              <div
                key={patient.patient._id}
                onClick={() => navigate(`/patient/${patient.patient._id}`)}
                className="bg-white rounded-2xl shadow-premium hover:shadow-premium-hover transition-all duration-500 border border-outline-variant/10 overflow-hidden group cursor-pointer relative"
                style={{ animationDelay: `${idx * 0.04}s` }}
              >
                {/* Severity accent bar */}
                <div className="absolute left-0 top-0 bottom-0 w-1 rounded-l-[2rem]" style={{ backgroundColor: cfg.hex }} />

                {/* Decorative gradient */}
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-transparent to-transparent group-hover:to-primary/[0.02] transition-all duration-500 pointer-events-none" />

                <div className="p-6 pl-7 relative z-10">
                  <div className="flex items-start gap-5">
                    {/* Risk Score Circle */}
                    <div className={`w-16 h-16 rounded-2xl flex flex-col items-center justify-center flex-shrink-0 ${cfg.bg} ring-2 ${cfg.ring}`}>
                      <span className={`text-2xl font-extrabold tracking-tighter ${cfg.text}`}>{score}</span>
                      <span className="text-xs font-bold uppercase tracking-wider text-slate-500">Risk</span>
                    </div>

                    {/* Patient Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <div className="flex items-center gap-3">
                            <h3 className="font-headline text-lg font-bold text-slate-900 group-hover:text-primary transition-colors break-words">
                              {patient.patient.name}
                            </h3>
                            <span className={`text-xs font-extrabold px-3 py-1 rounded-full border uppercase ${cfg.bg} ${cfg.text} ${cfg.border}`}>
                              {getRiskLabel(score)}
                            </span>
                          </div>
                          <p className="text-xs text-slate-500 font-medium mt-0.5">{patient.patient.email}</p>
                        </div>
                        <ChevronRight className="w-5 h-5 text-slate-300 group-hover:text-primary group-hover:translate-x-0.5 transition-all flex-shrink-0 mt-1" />
                      </div>

                      {/* Meta row */}
                      <div className="flex items-center gap-4 mt-3">
                        <span className="flex items-center gap-1.5 text-xs font-semibold text-slate-500">
                          <Clock className="w-3.5 h-3.5" />
                          {new Date(patient.lastActivity).toLocaleDateString()}
                        </span>
                        <span className="flex items-center gap-1.5 text-xs font-semibold text-slate-500">
                          {getTrendIcon(assessment?.trend)}
                          <span className="capitalize">{assessment?.trend || 'stable'}</span>
                        </span>
                        {assessment?.riskFactors?.length > 0 && (
                          <span className="text-xs font-medium text-slate-500 break-words">
                            {assessment.riskFactors.slice(0, 2).map(f => f.factor).join(' · ')}
                            {assessment.riskFactors.length > 2 && ` +${assessment.riskFactors.length - 2}`}
                          </span>
                        )}
                      </div>

                      {/* Risk breakdown meters */}
                      <div className="flex gap-6 mt-4">
                        {[
                          { label: 'Readmission', val: patient.riskScore.readmission ?? patient.riskScore.readmissionRisk ?? 0 },
                          { label: 'Deterioration', val: patient.riskScore.deterioration ?? patient.riskScore.deteriorationRisk ?? 0 },
                          { label: 'Medication', val: patient.riskScore.medicationAdherence ?? patient.riskScore.medicationAdherenceRisk ?? 0 },
                        ].map((m, i) => {
                          const mKey = getRiskKey(m.val);
                          const mCfg = riskCfg[mKey];
                          return (
                            <div key={i} className="flex-1 max-w-[140px]">
                              <div className="flex items-center justify-between mb-1">
                                <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">{m.label}</span>
                                <span className={`text-xs font-extrabold ${mCfg.text}`}>{m.val}</span>
                              </div>
                              <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                                <div
                                  className="h-full rounded-full transition-all duration-700"
                                  style={{ width: `${Math.min(m.val, 100)}%`, backgroundColor: mCfg.hex }}
                                />
                              </div>
                            </div>
                          );
                        })}
                      </div>

                      {/* Recommended action */}
                      {recommendedAction && (
                        <div className="mt-4 pt-4 border-t border-outline-variant/10 flex items-center justify-between gap-4">
                          <div className="flex items-center gap-3 min-w-0">
                            <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-primary/10 flex-shrink-0">
                              <Stethoscope className="w-4 h-4 text-primary" />
                            </div>
                            <div className="min-w-0">
                              <p className="text-xs font-extrabold text-slate-500 uppercase tracking-[0.15em]">Recommended Action</p>
                              <p className="text-sm font-bold text-slate-800 break-words">{recommendedAction.actionType.replace(/_/g, ' ')}</p>
                            </div>
                            <span className={`text-xs font-extrabold px-2.5 py-1 rounded-full border uppercase flex-shrink-0 ${
                              recommendedAction.priority === 'critical' ? 'bg-red-50 text-red-600 border-red-100' :
                              recommendedAction.priority === 'high' ? 'bg-orange-50 text-orange-600 border-orange-100' :
                              'bg-amber-50 text-amber-700 border-amber-100'
                            }`}>
                              {recommendedAction.priority}
                            </span>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <button
                              onClick={(e) => handleActionClick(patient, recommendedAction, e)}
                              className="flex items-center gap-2 bg-slate-900 text-white px-4 py-2 rounded-xl text-xs font-bold hover:bg-primary shadow-sm hover:shadow-primary/25 transition-all duration-300 active:scale-95"
                            >
                              Take Action
                              <ArrowRight className="w-3.5 h-3.5" />
                            </button>
                            <button
                              onClick={(e) => handleDismissAlert(assessment, patient.patient._id, e)}
                              className="px-4 py-2 text-xs font-bold text-slate-500 hover:text-slate-600 hover:bg-slate-50 rounded-xl transition-all"
                            >
                              Dismiss
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>

      {/* ── Action Slide-Over Panel ── */}
      {actionPanel && (() => {
        const { patient, assessment, riskScore, action } = actionPanel;
        const score = riskScore?.overall ?? assessment?.riskScore?.overall ?? 0;
        const key = getRiskKey(score);
        const cfg = riskCfg[key];

        // Map actionType → icon + description + form hint
        const actionMeta = {
          emergency_referral:        { icon: AlertTriangle, color: '#ba1a1a', label: 'Emergency Referral',        hint: 'Document the emergency and confirm referral details.' },
          urgent_clinical_review:    { icon: Stethoscope,   color: '#F97316', label: 'Urgent Clinical Review',    hint: 'Review the patient chart and document your clinical assessment.' },
          care_coordinator_outreach: { icon: Phone,          color: '#00478d', label: 'Care Coordinator Outreach', hint: 'Assign a care coordinator and note coordination plan.' },
          pharmacy_outreach:         { icon: Pill,           color: '#7c3aed', label: 'Pharmacy Outreach',         hint: 'Contact pharmacy team regarding medication adherence.' },
          medication_review:         { icon: Pill,           color: '#7c3aed', label: 'Medication Review',         hint: 'Review current medications and document any changes.' },
          schedule_home_visit:       { icon: Calendar,       color: '#00696b', label: 'Schedule Home Visit',       hint: 'Arrange a home health visit and confirm timing.' },
          schedule_virtual_visit:    { icon: MessageSquare,  color: '#00696b', label: 'Schedule Virtual Visit',    hint: 'Send a telehealth link and confirm availability.' },
          caregiver_notification:    { icon: Phone,          color: '#5f4300', label: 'Caregiver Notification',    hint: 'Notify the patient caregiver of the current risk status.' },
          self_care_guidance:        { icon: Heart,          color: '#00696b', label: 'Self-Care Guidance',        hint: 'Send patient self-care instructions and follow-up schedule.' },
          follow_up_scheduling:      { icon: Calendar,       color: '#00478d', label: 'Follow-Up Scheduling',      hint: 'Book a follow-up appointment within the recommended window.' },
        };
        const meta = actionMeta[action.actionType] || { icon: FileText, color: '#334155', label: action.actionType.replace(/_/g, ' '), hint: 'Document your action and any relevant notes.' };
        const ActionIcon = meta.icon;
        const isCritical = action.priority === 'critical';

        return (
          <>
            {/* Backdrop */}
            <div
              className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-40 transition-opacity"
              onClick={closePanel}
            />

            {/* Panel */}
            <div
              ref={panelRef}
              className="fixed right-0 top-0 bottom-0 w-full max-w-md bg-white z-50 shadow-2xl flex flex-col"
              style={{ animation: 'slideInRight 0.22s cubic-bezier(0.16,1,0.3,1)' }}
            >
              {/* Panel header */}
              <div className="flex items-center justify-between px-6 py-5 border-b border-slate-100">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${meta.color}18` }}>
                    <ActionIcon className="w-5 h-5" style={{ color: meta.color }} />
                  </div>
                  <div>
                    <p className="text-xs font-extrabold text-slate-400 uppercase tracking-widest">Take Action</p>
                    <h3 className="font-headline text-base font-extrabold text-slate-900 leading-tight capitalize">{meta.label}</h3>
                  </div>
                </div>
                <button onClick={closePanel} className="p-2 hover:bg-slate-100 rounded-xl transition-colors">
                  <X className="w-5 h-5 text-slate-500" />
                </button>
              </div>

              {/* Patient context strip */}
              <div className={`px-6 py-4 border-b border-slate-100 flex items-center gap-4`}>
                <div className={`w-12 h-12 rounded-xl flex flex-col items-center justify-center flex-shrink-0 ${cfg.bg} ring-2 ${cfg.ring}`}>
                  <span className={`text-lg font-extrabold tracking-tighter ${cfg.text}`}>{score}</span>
                  <span className="text-[10px] font-bold uppercase tracking-wide text-slate-500">Risk</span>
                </div>
                <div className="min-w-0">
                  <p className="font-bold text-slate-900 truncate">{patient.name}</p>
                  <p className="text-xs text-slate-500 font-medium truncate">{patient.email}</p>
                  <span className={`inline-block mt-1 text-xs font-extrabold px-2 py-0.5 rounded-full border uppercase ${cfg.bg} ${cfg.text} ${cfg.border}`}>
                    {getRiskLabel(score)} risk
                  </span>
                </div>
                <button
                  onClick={() => { closePanel(); navigate(`/patient/${patient._id}`); }}
                  className="ml-auto flex items-center gap-1 text-xs font-bold text-primary hover:text-primary/80 flex-shrink-0"
                >
                  Full chart
                  <ExternalLink className="w-3.5 h-3.5" />
                </button>
              </div>

              {/* Scrollable body */}
              <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">

                {/* Risk factors context */}
                {assessment?.riskFactors?.length > 0 && (
                  <div className="bg-slate-50 rounded-xl p-4 border border-slate-100">
                    <p className="text-xs font-extrabold text-slate-500 uppercase tracking-widest mb-2">Risk Factors</p>
                    <div className="flex flex-wrap gap-1.5">
                      {assessment.riskFactors.slice(0, 5).map((f, i) => (
                        <span key={i} className="text-xs font-semibold px-2 py-1 bg-white border border-slate-200 rounded-lg text-slate-700">
                          {f.factor}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Action instruction */}
                {isCritical && (
                  <div className="flex items-start gap-3 p-3 bg-red-50 border border-red-100 rounded-xl">
                    <AlertTriangle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                    <p className="text-sm font-semibold text-red-700">Critical priority — immediate response required.</p>
                  </div>
                )}

                <div className="bg-primary/5 border border-primary/10 rounded-xl p-4">
                  <p className="text-xs font-extrabold text-primary uppercase tracking-widest mb-1">What to do</p>
                  <p className="text-sm text-slate-700 font-medium">{meta.hint}</p>
                </div>

                {/* Notes field */}
                <div>
                  <label className="text-xs font-extrabold text-slate-500 uppercase tracking-[0.15em] mb-2 block">Clinical Notes <span className="normal-case font-normal text-slate-400">(optional)</span></label>
                  <textarea
                    value={panelNote}
                    onChange={e => setPanelNote(e.target.value)}
                    rows={4}
                    placeholder={`Document your ${meta.label.toLowerCase()} notes here...`}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm font-medium text-slate-900 focus:bg-white focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all resize-none"
                  />
                </div>
              </div>

              {/* Footer actions */}
              <div className="px-6 py-5 border-t border-slate-100 space-y-2">
                <button
                  onClick={executeAction}
                  disabled={executing}
                  className="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl font-extrabold text-sm text-white shadow-sm transition-all active:scale-[0.98] disabled:opacity-50"
                  style={{ backgroundColor: meta.color }}
                >
                  {executing ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                  {executing ? 'Recording...' : `Confirm — ${meta.label}`}
                </button>
                <button
                  onClick={async (e) => {
                    await handleDismissAlert(actionPanel.assessment, patient._id, e);
                    closePanel();
                  }}
                  disabled={executing}
                  className="w-full py-3 rounded-xl font-bold text-sm text-slate-500 hover:bg-slate-50 border border-slate-200 transition-all disabled:opacity-50"
                >
                  Dismiss Alert
                </button>
              </div>
            </div>

            <style>{`
              @keyframes slideInRight {
                from { transform: translateX(100%); opacity: 0.8; }
                to   { transform: translateX(0);    opacity: 1; }
              }
            `}</style>
          </>
        );
      })()}
    </>
  );
};

export default PriorityDashboard;
